import { z } from "zod";

import { topicDataSchema } from "../topic-data";

export const CAMERA_TYPE = {
  orbit: "orbit",
  thirdPerson: "third_person",
} as const;

export const orbitCameraSchema = z.strictObject({
  type: z.literal(CAMERA_TYPE.orbit),
  target: z.object({
    id: z.string(),
    mesh: z.enum(["mesh", "path"] as const),
  }),
  zoom: z.object({
    min: z.number().optional(),
    max: z.number().optional(),
  }),
});

export type OrbitCamera = z.infer<typeof orbitCameraSchema>;

/**
 * RTS-style third-person camera that orbits a free-floating world-space
 * focus point. Decoupled from any tracked entity: the camera stays put
 * during playback, and the user moves the focus point directly via pan
 * input. Radius is clamped to the hardcoded `[2, 100]` envelope and beta
 * to `[0.01, pi - 0.01]` at runtime.
 *
 * Numeric fields use `.finite()` to reject `NaN` and `±Infinity`, which
 * would propagate through the view-matrix math and corrupt rendering.
 * Out-of-envelope finite values are accepted at the schema layer and
 * clamped at runtime by `constrainCamera` (radius) and Babylon's
 * `lowerBetaLimit`/`upperBetaLimit` (beta).
 */
export const thirdPersonCameraSchema = z.strictObject({
  type: z.literal(CAMERA_TYPE.thirdPerson),
  focusPoint: z.strictObject({
    x: z.number().finite(),
    y: z.number().finite(),
    z: z.number().finite(),
  }),
  alpha: z.number().finite(),
  beta: z.number().finite(),
  radius: z.number().finite(),
});

export type ThirdPersonCamera = z.infer<typeof thirdPersonCameraSchema>;

export const cameraSchema = z.discriminatedUnion("type", [
  orbitCameraSchema,
  thirdPersonCameraSchema,
]);

export type Camera = z.infer<typeof cameraSchema>;

export const pathSchema = z.strictObject({
  color: z.string(),
  lineWidth: z.number().optional(),
});

export type Path = z.infer<typeof pathSchema>;

export const positionTransformationSchema = z.literal("lla_to_enu").nullable();

export type PositionTransformation = z.infer<
  typeof positionTransformationSchema
>;

export const positionBasePresetSchema = z.enum([
  "lat_lon_alt",
  "east_north_up",
  "north_east_down",
  "forward_right_up",
] as const);

export type PositionBasePreset = z.infer<typeof positionBasePresetSchema>;

export const positionSchema = z.strictObject({
  transformation: positionTransformationSchema.optional(),
  basePreset: positionBasePresetSchema.optional(),
  forward: z.union([topicDataSchema, z.number()]),
  forwardScale: z.number(),
  right: z.union([topicDataSchema, z.number()]),
  rightScale: z.number(),
  up: z.union([topicDataSchema, z.number()]),
  upScale: z.number(),
});

export type Position = z.infer<typeof positionSchema>;

export const sphereSchema = z.strictObject({
  type: z.literal("sphere"),
  color: z.string(),
  radius: z.number(),
});

export type Sphere = z.infer<typeof sphereSchema>;

export const boxSchema = z.strictObject({
  type: z.literal("box"),
  color: z.string(),
  size: z.number(),
});

export type Box = z.infer<typeof boxSchema>;

export const meshSchema = z.discriminatedUnion("type", [
  sphereSchema,
  boxSchema,
]);

export type Mesh = z.infer<typeof meshSchema>;

const vectorFieldDisplayModeSchema = z.enum([
  "field_only",
  "current_only",
] as const);

export type VectorFieldDisplayMode = z.infer<
  typeof vectorFieldDisplayModeSchema
>;

export const vectorFieldSchema = z.strictObject({
  forward: z.union([topicDataSchema, z.number()]),
  forwardScale: z.number(),
  right: z.union([topicDataSchema, z.number()]),
  rightScale: z.number(),
  up: z.union([topicDataSchema, z.number()]),
  upScale: z.number(),
  color: z.string(),
  lineWidth: z.number().gt(0),
  stride: z.number().gte(1).optional(),
  displayMode: vectorFieldDisplayModeSchema.optional(),
  gradientEnabled: z.boolean().optional(),
  colorLow: z.string().optional(),
  colorHigh: z.string().optional(),
});

export type VectorField = z.infer<typeof vectorFieldSchema>;

export const gridSchema = z.strictObject({
  color: z.string(),
  scale: z.number().optional(),
  verticalOffset: z.number().optional(),
});

export type Grid = z.infer<typeof gridSchema>;

export const entitySchema = z.strictObject({
  id: z.string(),
  position: positionSchema.optional(),
  mesh: meshSchema.optional(),
  path: pathSchema.optional(),
  vectorField: vectorFieldSchema.optional(),
});

export type Entity = z.infer<typeof entitySchema>;

export type EntityID = string;

export const threeDPanelConfigSchema = z.strictObject({
  camera: cameraSchema.optional(),
  grid: gridSchema.optional(),
});

export type PanelConfig = z.infer<typeof threeDPanelConfigSchema>;

export const threeDPanelDataSchema = z.strictObject({
  entities: z
    .record(z.string(), entitySchema)
    .refine(
      (v) => Object.keys(v).length >= 1,
      "entities must have at least 1 entry",
    ),
});

export type PanelData = z.infer<typeof threeDPanelDataSchema>;
